import { useQuery } from '@tanstack/react-query';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { buildPipelineCounts } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { useDashboardDateRange, type DashboardRange } from '@/features/dashboard/hooks';
import { buildChartDates, buildLineChartSeries } from '@/features/executions/utils/chartData';
import { buildStackedExecutionChartData } from '@/features/executions/utils/stackedExecutionChart';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { buildThresholdGuideLines } from '@/features/metrics/lib/chartThresholdGuides';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryPipelineSummary } from '@/features/pipelines/data';
import { useProjectMetricThresholds } from '@/features/projects';
import { useProjectFilter } from '@/features/projects';
import {
  buildSelectedDayRangeFilters,
  resolveChartDaySelection,
} from '@/features/shared/hooks/detailDateHelpers';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { queryKeys, useAccessToken, usePersistedStringArray, useTagOptions } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { MetricsRange } from '@/features/metrics/metricsHelpers';
import type { PipelineDetailDto } from '@flowqa/shared';

type PipelineDetailPageViewModelParams = {
  pipeline: PipelineDetailDto | null | undefined;
};

type PipelineChartTab = 'status' | 'pass-rate' | 'frequency' | 'duration';

export type PipelineDetailPageViewModel = {
  title: string;
  pipelineId: string;
  chartTab: PipelineChartTab;
  setChartTab: Dispatch<SetStateAction<PipelineChartTab>>;
  suppressChartAnimation: boolean;
  range: DashboardRange;
  setRange: Dispatch<SetStateAction<DashboardRange>>;
  selectedStartDay: string | null;
  setSelectedStartDay: Dispatch<SetStateAction<string | null>>;
  selectedEndDay: string | null;
  setSelectedEndDay: Dispatch<SetStateAction<string | null>>;
  isAllMode: boolean;
  pickerStartDay: string | null;
  pickerEndDay: string | null;
  handleStartDayChange: (nextDay: string | null) => void;
  handleEndDayChange: (nextDay: string | null) => void;
  pipelineMetricCards: ReturnType<typeof buildStatusMetricCards>;
  metricValueItems: ReturnType<typeof buildMetricValueItems>;
  showChartDateClear: boolean;
  handleChartDateClear: () => void;
  stackedChartData: ReturnType<typeof buildStackedExecutionChartData>;
  passRateChartData: ReturnType<typeof buildLineChartSeries> | [] | null;
  frequencyChartData: ReturnType<typeof buildLineChartSeries> | [] | null;
  durationChartData: ReturnType<typeof buildLineChartSeries> | [] | null;
  passRateGuideLines: ReturnType<typeof buildThresholdGuideLines>;
  frequencyGuideLines: ReturnType<typeof buildThresholdGuideLines>;
  durationGuideLines: ReturnType<typeof buildThresholdGuideLines>;
  handleChartSelect: (dataIndex: number) => void;
  showLineChartPoints: boolean;
  selectedTags: string[];
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  handleTagsChange: (next: string[]) => void;
  buildsTableFilters: ReturnType<typeof mergeFilters>;
};

const usePipelineDetailPageViewModelInternal = ({
  pipeline,
}: PipelineDetailPageViewModelParams): PipelineDetailPageViewModel => {
  const title = pipeline?.name ?? '';
  const pipelineId = pipeline?.id ?? '';
  const [chartTab, setChartTab] = useState<'status' | 'pass-rate' | 'frequency' | 'duration'>(
    'status',
  );
  const [suppressChartAnimation, setSuppressChartAnimation] = useState(false);
  const [range, setRange] = useState<DashboardRange>('7d');
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:pipeline-detail');
  const accessToken = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: selectedProjectId,
    enabled: Boolean(accessToken),
  });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useDashboardDateRange(range);
  const isAllMode = range === 'all';
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);
  const showStatusDelta = Boolean(
    !isAllMode && !hasCustomDates && previousRangeStartIso && previousRangeEndIso,
  );
  const queryEnabled = Boolean(accessToken && pipeline && selectedProjectId);

  const metricsRange = useMemo<MetricsRange>(
    () => (hasCustomDates ? 'all' : range),
    [hasCustomDates, range],
  );
  const basePipelineFilters = useMemo(() => buildEqFilterValue('id', pipelineId), [pipelineId]);
  const dateRangeFilters = useMemo(
    () => buildDateRangeFilters('startedAt', rangeStartIso, rangeEndIso),
    [rangeEndIso, rangeStartIso],
  );
  const selectedDayFilters = useMemo(() => {
    return buildSelectedDayRangeFilters('startedAt', selectedStartDay, selectedEndDay);
  }, [selectedEndDay, selectedStartDay]);
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const buildsTableFilters = useMemo(
    () =>
      mergeFilters(
        buildEqFilterValue('pipelineId', pipelineId),
        selectedDayFilters,
        !isAllMode ? dateRangeFilters : undefined,
        tagFilters,
      ),
    [dateRangeFilters, isAllMode, pipelineId, selectedDayFilters, tagFilters],
  );

  const summaryInput = useMemo(
    () => ({
      page: 1,
      pageSize: 1,
      sorts: [],
      filters: basePipelineFilters,
      metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? undefined,
      metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? undefined,
    }),
    [basePipelineFilters, rangeEndIso, rangeStartIso, selectedEndDay, selectedStartDay],
  );
  const previousSummaryInput = useMemo(
    () =>
      showStatusDelta
        ? {
            page: 1,
            pageSize: 1,
            sorts: [],
            filters: basePipelineFilters,
            metricsStartDay: previousRangeStartIso?.slice(0, 10) ?? undefined,
            metricsEndDay: previousRangeEndIso?.slice(0, 10) ?? undefined,
          }
        : null,
    [basePipelineFilters, previousRangeEndIso, previousRangeStartIso, showStatusDelta],
  );
  const summaryFiltersKey = useMemo(
    () =>
      JSON.stringify({
        metricsStartDay: summaryInput.metricsStartDay ?? null,
        metricsEndDay: summaryInput.metricsEndDay ?? null,
        filters: summaryInput.filters,
      }),
    [summaryInput.filters, summaryInput.metricsEndDay, summaryInput.metricsStartDay],
  );
  const previousSummaryFiltersKey = useMemo(
    () =>
      previousSummaryInput
        ? JSON.stringify({
            metricsStartDay: previousSummaryInput.metricsStartDay ?? null,
            metricsEndDay: previousSummaryInput.metricsEndDay ?? null,
            filters: previousSummaryInput.filters,
          })
        : 'latest',
    [previousSummaryInput],
  );
  const pipelineSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      summaryFiltersKey,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await queryPipelineSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        summaryInput,
        accessToken,
      ),
  });
  const previousPipelineSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousSummaryFiltersKey,
    ),
    enabled: Boolean(queryEnabled && previousSummaryInput),
    queryFn: async () =>
      await queryPipelineSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousSummaryInput ?? summaryInput,
        accessToken,
      ),
  });
  const pipelineSummary = pipelineSummaryQuery.data ?? null;
  const previousPipelineSummary = showStatusDelta
    ? (previousPipelineSummaryQuery.data ?? null)
    : null;
  const pipelineCounts = useMemo(() => buildPipelineCounts(pipelineSummary), [pipelineSummary]);
  const previousPipelineCounts = useMemo(
    () => buildPipelineCounts(previousPipelineSummary),
    [previousPipelineSummary],
  );
  const pipelineDeltas = useMemo(
    () =>
      showStatusDelta && previousPipelineSummary
        ? buildStatusDeltaMap(pipelineCounts, previousPipelineCounts)
        : undefined,
    [pipelineCounts, previousPipelineCounts, previousPipelineSummary, showStatusDelta],
  );
  const pipelineMetricOptions = useMemo(() => {
    const baseOptions = mapStatusOptionsToMetricOptions(buildStatusOptions, resolveMetricTone);
    return [
      ...baseOptions,
      { value: 'none' as const, label: 'No Builds', tone: resolveMetricTone('none') },
    ];
  }, []);
  const pipelineMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: pipelineMetricOptions,
        counts: pipelineCounts,
        deltas: pipelineDeltas,
        total: pipelineSummary?.total,
        loading: pipelineSummaryQuery.isLoading,
        showDelta: showStatusDelta,
      }),
    [
      pipelineCounts,
      pipelineDeltas,
      pipelineMetricOptions,
      pipelineSummary?.total,
      pipelineSummaryQuery.isLoading,
      showStatusDelta,
    ],
  );
  const metricsQuery = useMetricsQuery({
    range: metricsRange,
    statuses: [],
    scope: 'pipeline',
    projectId: selectedProjectId,
    entityId: pipelineId,
    name: pipeline?.name ?? '',
    startDay: hasCustomDates ? selectedStartDay : null,
    endDay: hasCustomDates ? selectedEndDay : null,
    enabled: queryEnabled,
  });
  const previousStartDay = previousRangeStartIso?.slice(0, 10) ?? null;
  const previousEndDay = previousRangeEndIso?.slice(0, 10) ?? null;
  const showMetricDelta = Boolean(
    !isAllMode && !hasCustomDates && previousStartDay && previousEndDay,
  );
  const previousMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: [],
    scope: 'pipeline',
    projectId: selectedProjectId,
    entityId: pipelineId,
    name: pipeline?.name ?? '',
    startDay: previousStartDay,
    endDay: previousEndDay,
    enabled: queryEnabled && showMetricDelta,
  });
  const summaryPassRate = pipelineSummary?.averageExecutionPassRate ?? null;
  const previousSummaryPassRate = showStatusDelta
    ? (previousPipelineSummary?.averageExecutionPassRate ?? null)
    : null;
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: metricsQuery.data ?? null,
        previousMetrics: showMetricDelta ? (previousMetricsQuery.data ?? null) : null,
        passRateOverride: summaryPassRate,
        previousPassRateOverride: previousSummaryPassRate,
        thresholds: thresholds.pipelines,
        showDelta: showMetricDelta,
      }),
    [
      metricsQuery.data,
      previousMetricsQuery.data,
      previousSummaryPassRate,
      showMetricDelta,
      summaryPassRate,
      thresholds.pipelines,
    ],
  );
  const chartDates = useMemo(() => {
    const labels = metricsQuery.data?.metricsRangeLabels ?? [];
    const dates = metricsQuery.data?.metricsRangeDates ?? [];
    return buildChartDates(labels, dates);
  }, [metricsQuery.data?.metricsRangeDates, metricsQuery.data?.metricsRangeLabels]);
  const lineChartLabels = metricsQuery.data?.metricsRangeLabels ?? [];
  const stackedChartData = useMemo(
    () => buildStackedExecutionChartData(metricsQuery.data),
    [metricsQuery.data],
  );
  const passRateChartData = useMemo(() => {
    const series = buildLineChartSeries(
      metricsQuery.data?.series.filteredPassRate,
      lineChartLabels,
      chartDates,
      'passRate',
    );
    if (series) return series;
    return metricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    lineChartLabels,
    metricsQuery.data?.series.filteredPassRate,
    metricsQuery.isFetched,
  ]);
  const frequencyChartData = useMemo(() => {
    const series = buildLineChartSeries(
      metricsQuery.data?.series.executionTrendMetrics,
      lineChartLabels,
      chartDates,
      'frequency',
    );
    if (series) return series;
    return metricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    lineChartLabels,
    metricsQuery.data?.series.executionTrendMetrics,
    metricsQuery.isFetched,
  ]);
  const durationChartData = useMemo(() => {
    const series = buildLineChartSeries(
      metricsQuery.data?.series.executionDurationAverage,
      lineChartLabels,
      chartDates,
      'duration',
    );
    if (series) return series;
    return metricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    lineChartLabels,
    metricsQuery.data?.series.executionDurationAverage,
    metricsQuery.isFetched,
  ]);
  const handleChartSelect = useCallback(
    (dataIndex: number) => {
      const day = chartDates[dataIndex];
      const nextSelection = resolveChartDaySelection(selectedStartDay, selectedEndDay, day);
      setSelectedStartDay(nextSelection.startDay);
      setSelectedEndDay(nextSelection.endDay);
    },
    [chartDates, selectedEndDay, selectedStartDay],
  );
  const showLineChartPoints = Boolean(
    selectedStartDay && selectedEndDay && selectedStartDay === selectedEndDay,
  );
  const showChartDateClear = Boolean(selectedStartDay ?? selectedEndDay);
  const handleChartDateClear = useCallback(() => {
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);
  const passRateGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.pipelines.passRate),
    [thresholds.pipelines.passRate],
  );
  const frequencyGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.pipelines.frequency),
    [thresholds.pipelines.frequency],
  );
  const durationGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.pipelines.avgDuration),
    [thresholds.pipelines.avgDuration],
  );
  const pickerStartDay = useMemo(() => {
    if (selectedStartDay) return selectedStartDay;
    if (!isAllMode) return rangeStartIso ? rangeStartIso.slice(0, 10) : null;
    return chartDates[0] ?? null;
  }, [chartDates, isAllMode, rangeStartIso, selectedStartDay]);
  const pickerEndDay = useMemo(() => {
    if (selectedEndDay) return selectedEndDay;
    if (!isAllMode) return rangeEndIso ? rangeEndIso.slice(0, 10) : null;
    return chartDates.length > 0 ? (chartDates[chartDates.length - 1] ?? null) : null;
  }, [chartDates, isAllMode, rangeEndIso, selectedEndDay]);
  const handleStartDayChange = useMemo(
    () => (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleEndDayChange = useMemo(
    () => (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );

  useEffect(() => {
    setSuppressChartAnimation(true);
    const timer = window.setTimeout(() => {
      setSuppressChartAnimation(false);
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [chartTab]);

  return {
    title,
    pipelineId,
    chartTab,
    setChartTab,
    suppressChartAnimation,
    range,
    setRange,
    selectedStartDay,
    setSelectedStartDay,
    selectedEndDay,
    setSelectedEndDay,
    isAllMode,
    pickerStartDay,
    pickerEndDay,
    handleStartDayChange,
    handleEndDayChange,
    pipelineMetricCards,
    metricValueItems,
    showChartDateClear,
    handleChartDateClear,
    stackedChartData,
    passRateChartData,
    frequencyChartData,
    durationChartData,
    passRateGuideLines,
    frequencyGuideLines,
    durationGuideLines,
    handleChartSelect,
    showLineChartPoints,
    selectedTags,
    tagOptions,
    tagOptionsLoading,
    handleTagsChange: setSelectedTags,
    buildsTableFilters,
  };
};

export const usePipelineDetailPageViewModel = (
  params: PipelineDetailPageViewModelParams,
): PipelineDetailPageViewModel => usePipelineDetailPageViewModelInternal(params);
